import { apiSlice } from "../../../features/api/apiSlice";
import {
  ChapterTreasurerDashboard,
  FinanceAnalytics,
  MemberStatement,
} from "../types/finance";

export type UserCategory = {
  id: number;
  name: string;
  is_active: boolean;
};
type UserCategoryResponse = {
  data: UserCategory[];
};

export type MembershipBand = {
  id: number;
  user_category_id: number;
  label: string;
  min_years?: number | null;
  max_years?: number | null;
  discount_percentage?: number;
  is_active: boolean;
  category?: UserCategory;
};

export type ChargeType = {
  id: number;
  name: string;
  code: string;
  type: "annual" | "one_time";
  description?: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
};

export type ChargeTypePayload = {
  name: string;
  code: string;
  type: "annual" | "one_time";
  description?: string | null;
  is_active?: boolean;
};

export type ChargePrice = {
  id: number;
  charge_type_id: number;
  user_category_id?: number | null;
  membership_band_id?: number | null;
  year?: number | null;
  amount: number;
  currency: string;
  is_active: boolean;
  chargeType?: ChargeType;
  category?: UserCategory;
  membershipBand?: MembershipBand;
  created_at?: string;
  updated_at?: string;
};

export type ChargePricePayload = {
  charge_type_id: number;
  user_category_id?: number | null;
  membership_band_id?: number | null;
  year?: number | null;
  amount: number;
  currency?: string;
  is_active?: boolean;
};

export type DuesItem = {
  charge_type_id: number;
  code: string;
  name: string;
  type: "annual" | "one_time";
  year?: number | null;
  currency: string;

  base_amount: number;
  discount_percentage: number;
  discount_amount: number;
  final_amount: number;

  already_paid: boolean;
  payable: boolean;
};

export type DuesResponse = {
  year: number;
  user_id: string | number;
  total_payable: number;
  items: DuesItem[];
};

// Charge inside one-time or annual
export interface FinanceCharge {
  charge_price_id: number;
  charge_name: string;
  amount: number;
  paid: number;
  balance: number;
  status: "paid" | "unpaid";
}

// One-time section
export interface OneTimeSummary {
  total_expected: number;
  total_paid: number;
  total_owed: number;
  charges: FinanceCharge[];
}

// Annual grouped by year
export interface AnnualYearGroup {
  year: number;
  total_expected: number;
  total_paid: number;
  total_owed: number;
  charges: FinanceCharge[];
}

// Grand totals
export interface GrandSummary {
  total_expected: number;
  total_paid: number;
  total_owed: number;
  advance?: any;
}

// User block
export interface FinanceUser {
  id: number;
  name: string;
  category?: string | null;
  membership_band?: string | null;
}

// FULL API RESPONSE
export interface FinanceSummaryResponse {
  user: FinanceUser;
  grand_summary: GrandSummary;
  one_time: OneTimeSummary;
  annual: AnnualYearGroup[];
}

function pick(res: any) {
  console.log("this is the picked resss ponse before pic", res);
  return res?.data?.data ?? res?.data ?? res;
}

// ✅ ALWAYS returns an array
function pickArray(res: any) {
  const d = pick(res);
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(res?.data)) return res.data;

  return [];
}

export const billingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* =====================
     * LOOKUPS (optional)
     * ===================== */
    getUserCategories: builder.query<any, void>({
      query: () => "/admin/billing/user-categories",
      transformResponse: (res: any) => pickArray(res),
      providesTags: (result) => {
        const list = Array.isArray(result) ? result : [];
        return list.length
          ? [
              ...list.map((x) => ({
                type: "UserCategories" as const,
                id: x.id,
              })),
              { type: "UserCategories" as const, id: "LIST" },
            ]
          : [{ type: "UserCategories" as const, id: "LIST" }];
      },
    }),

    getMembershipBands: builder.query<
      MembershipBand[],
      { user_category_id?: number } | void
    >({
      query: (arg) => {
        const user_category_id = (arg as any)?.user_category_id;
        return user_category_id
          ? `/members/membership-bands?user_category_id=${user_category_id}`
          : "/members/membership-bands";
      },
      transformResponse: (res: any) => {
        console.log({ ...res }, "checking the band to confirm");
        return pickArray(res);
      },
      providesTags: (result) => {
        const list = Array.isArray(result) ? result : [];
        return list.length
          ? [
              ...list.map((x) => ({
                type: "MembershipBands" as const,
                id: x.id,
              })),
              { type: "MembershipBands" as const, id: "LIST" },
            ]
          : [{ type: "MembershipBands" as const, id: "LIST" }];
      },
    }),

    /* =====================
     * CHARGE TYPES
     * ===================== */
    getChargeTypes: builder.query<ChargeType[], { q?: string } | void>({
      query: (arg) => {
        const q = (arg as any)?.q;
        return q
          ? `/admin/billing/charge-types?q=${encodeURIComponent(q)}`
          : "/admin/billing/charge-types";
      },
      transformResponse: (res: any) => pickArray(res),
      providesTags: (result) => {
        const list = Array.isArray(result) ? result : [];
        return list.length
          ? [
              ...list.map((x) => ({ type: "ChargeTypes" as const, id: x.id })),
              { type: "ChargeTypes" as const, id: "LIST" },
            ]
          : [{ type: "ChargeTypes" as const, id: "LIST" }];
      },
    }),

    getChargeTypeDetails: builder.query<ChargeType, number>({
      query: (id) => `/admin/billing/charge-types/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, id) => [{ type: "ChargeTypes" as const, id }],
    }),

    createChargeType: builder.mutation<any, ChargeTypePayload>({
      query: (body) => ({
        url: "/admin/billing/charge-types",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "ChargeTypes" as const, id: "LIST" }],
    }),

    updateChargeType: builder.mutation<
      any,
      { id: number; body: Partial<ChargeTypePayload> }
    >({
      query: ({ id, body }) => ({
        url: `/admin/billing/charge-types/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ChargeTypes" as const, id },
        { type: "ChargeTypes" as const, id: "LIST" },
      ],
    }),

    deleteChargeType: builder.mutation<any, number>({
      query: (id) => ({
        url: `/admin/billing/charge-types/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ChargeTypes" as const, id: "LIST" }],
    }),

    /* =====================
     * CHARGE PRICES
     * ===================== */
    getChargePrices: builder.query<
      { data: ChargePrice[]; meta?: any },
      {
        year?: number;
        charge_type_id?: number;
        user_category_id?: number;
        membership_band_id?: number;
      } | void
    >({
      query: (arg) => {
        const p = new URLSearchParams();
        const a = (arg as any) || {};
        if (a.year) p.set("year", String(a.year));
        if (a.charge_type_id) p.set("charge_type_id", String(a.charge_type_id));
        if (a.user_category_id)
          p.set("user_category_id", String(a.user_category_id));
        if (a.membership_band_id)
          p.set("membership_band_id", String(a.membership_band_id));
        const qs = p.toString();
        return qs
          ? `/admin/billing/charge-prices?${qs}`
          : "/admin/billing/charge-prices";
      },
      transformResponse: (res: any) => {
        const d = pick(res);
        // paginator shape: { data:[], ... }
        if (Array.isArray(d?.data)) return { data: d.data, meta: d };
        if (Array.isArray(d)) return { data: d };
        return { data: [] };
      },
      providesTags: (result) => {
        const { data = [] } = result ?? {};
        return data.length
          ? [
              ...data.map((x) => ({ type: "ChargePrices" as const, id: x.id })),
              { type: "ChargePrices" as const, id: "LIST" },
            ]
          : [{ type: "ChargePrices" as const, id: "LIST" }];
      },
    }),

    createChargePrice: builder.mutation<any, ChargePricePayload>({
      query: (body) => ({
        url: "/admin/billing/charge-prices",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "ChargePrices" as const, id: "LIST" }],
    }),

    updateChargePrice: builder.mutation<
      any,
      { id: number; body: Partial<ChargePricePayload> }
    >({
      query: ({ id, body }) => ({
        url: `/admin/billing/charge-prices/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ChargePrices" as const, id },
        { type: "ChargePrices" as const, id: "LIST" },
      ],
    }),

    deleteChargePrice: builder.mutation<any, number>({
      query: (id) => ({
        url: `/admin/billing/charge-prices/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ChargePrices" as const, id: "LIST" }],
    }),

    /* =====================
     * DUES
     * ===================== */
    getMyDues: builder.query<DuesResponse, { year?: number } | void>({
      query: (arg) => {
        const year = (arg as any)?.year;
        return year ? `/billing/dues?year=${year}` : "/billing/dues";
      },
      transformResponse: (res: any) => pick(res),
      providesTags: [{ type: "Dues" as const, id: "ME" }],
    }),

    getUserDues: builder.query<DuesResponse, { userId: string; year?: number }>(
      {
        query: ({ userId, year }) =>
          year
            ? `/admin/billing/users/${userId}/dues?year=${year}`
            : `/admin/billing/users/${userId}/dues`,
        transformResponse: (res: any) => pick(res),
        providesTags: (_r, _e, arg) => [
          { type: "Dues" as const, id: arg.userId },
        ],
      }
    ),

    /* =====================
     * FINANCE SUMMARY
     * ===================== */

    getMyFinanceSummary: builder.query<FinanceSummaryResponse, void>({
      query: () => ({
        url: "/billing/my/finance-summary",
        method: "GET",
      }),
    }),

    getUserFinanceSummary: builder.query<any, { userId: string | number }>({
      query: ({ userId }) => `/billing/${userId}/finance-summary`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, arg) => [
        { type: "FinanceSummary", id: arg.userId },
      ],
    }),
    uploadBankPaymentNow: builder.mutation({
      query: (formData: FormData) => ({
        url: "/admin/billing/upload-bank-payment",
        method: "POST",
        body: formData,
      }),
    }),
    createInvoice: builder.mutation({
      query: (body) => ({
        url: "/billing/invoices/create",
        method: "POST",
        body,
      }),
    }),
    getInvoice: builder.query({
      query: (id) => `/billing/invoices/${id}`,
      providesTags: ["Invoices"],
    }),
    getUserInvoice: builder.query({
      query: ({ userId, invoiceId }) =>
        `/billing/invoices/user/${userId}/${invoiceId}`,
      providesTags: ["Invoices"],
    }),
    getInvoiceItem: builder.query<any, string>({
      query: (id) => `/invoices/items/${id}`,
      providesTags: (result, error, id) => [{ type: "Invoices", id }],
    }),
    myInvoices: builder.query<any[], void>({
      query: () => "/billing/invoices/my",
      providesTags: ["Invoices"],
    }),
    userInvoices: builder.query<
      { user: any; invoices: any[] },
      { userId: string }
    >({
      query: ({ userId }) => `/billing/invoices/user/${userId}`,
      providesTags: ["Invoices"],
    }),
    payInvoice: builder.mutation({
      query: (data) => ({
        url: "/billing/payments",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Invoices", "Payments"],
    }),
    /*
    |--------------------------------------------------------------------------
    | GET PAYMENT / RECEIPT
    |--------------------------------------------------------------------------
    */

    getPayment: builder.query({
      query: (id) => `/billing/payments/${id}`,
      providesTags: ["Payments"],
    }),

    getMyFinanceSummary2: builder.query<any, void>({
      query: () => ({
        url: "/financial-status",
        method: "GET",
      }),
      providesTags: ["FinanceSummary"],
    }),

    /**
     * RECEIPTS
     * Used by:
     * - MyReceiptsPage
     */
    getMyReceipts: builder.query<any[], void>({
      query: () => ({
        url: "/my-receipts",
        method: "GET",
      }),
      providesTags: ["Receipts"],
    }),

    /**
     * DOWNLOAD RECEIPT
     */
    downloadReceipt: builder.query<any, number>({
      query: (receiptId) => ({
        url: `/finance/receipts/${receiptId}/download`,
        method: "GET",
      }),
    }),

    startInvoicePayment: builder.mutation<
      any,
      { invoiceId: string | number; amount?: number }
    >({
      query: ({ invoiceId, amount }) => ({
        url: `/payments/invoice/${invoiceId}/start`,
        method: "POST",
        // Laravel will receive this in $request->input('amount')
        body: amount ? { amount } : {},
      }),
    }),

    // Pay ALL outstanding dues in one OnePay transaction. Returns the OnePay
    // redirect payload (submit via submitOnePayForm).
    startDuesPayment: builder.mutation<any, void>({
      query: () => ({ url: "/billing/dues/pay-all", method: "POST" }),
    }),

    // The Index Query for Finance Management
    adminPaymentIndex: builder.query<any, any>({
      query: (params) => ({
        url: "/admin/billing/admin/payments",
        method: "GET",
        params: {
          page: params.page,
          per_page: params.per_page,
          search: params.search,
          sort_by: params.sort_by,
          sort_dir: params.sort_dir,
          status: params.status,
          payment_method: params.payment_method,
          year: params.year,
          start_date: params.start_date,
          end_date: params.end_date,
          admin_id: params.admin_id,
          chapter_id: params.chapter_id,
          state_id: params.state_id,
          user_category_id: params.user_category_id,
        },
      }),
      providesTags: ["Payments"],
    }),

    adminPaymentExport: builder.query<any[], any>({
      query: (params) => ({
        url: "/admin/billing/admin/payments/export",
        method: "GET",
        params: {
          search: params.search,
          sort_by: params.sort_by,
          sort_dir: params.sort_dir,
          status: params.status,
          year: params.year,
          start_date: params.start_date,
          end_date: params.end_date,
          admin_id: params.admin_id,
          chapter_id: params.chapter_id,
          state_id: params.state_id,
          user_category_id: params.user_category_id,
        },
      }),
    }),

    adminPayInvoice: builder.mutation({
      query: ({ invoiceId, formData }) => ({
        url: `/admin/billing/invoices/${invoiceId}/pay`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Invoices"],
    }),

    adminRecordAdvancePayment: builder.mutation({
      query: ({ userId, formData }) => ({
        url: `/admin/billing/users/${userId}/advance`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Invoices"],
    }),

    adminUserPayments: builder.query<any[], string>({
      query: (userId) => `/admin/billing/users/${userId}/payments`,
      providesTags: ["Payments"],
    }),

    getDuesAnalysis: builder.query<any, any>({
      query: (filters) => ({
        url: "/billing/dues-analysis",
        params: filters,
      }),
    }),

    getChapters: builder.query<any, void>({
      query: () => "/chapters",
    }),

    getStates: builder.query<any, void>({
      query: () => "/states",
    }),
    getFinanceDashboard: builder.query({
      query: (year) => ({
        url: "/billing/finance-dashboard",
        params: { year },
      }),
    }),

    // new ones
    /* =====================
   FINANCE ANALYTICS
===================== */

    getFinanceAnalytics: builder.query<FinanceAnalytics, any>({
      query: (params) => ({
        url: "/finance/analytics",
        params,
      }),
    }),

    getRevenueByChapter: builder.query<any[], { year?: number }>({
      query: (params) => ({
        url: "/finance/revenue-by-chapter",
        params,
      }),
      transformResponse: (res: any) => pickArray(res),
    }),

    getRevenueByState: builder.query<any[], { year?: number }>({
      query: (params) => ({
        url: "/finance/revenue-by-state",
        params,
      }),
      transformResponse: (res: any) => pickArray(res),
    }),

    getRevenueByBand: builder.query<any[], { year?: number }>({
      query: (params) => ({
        url: "/finance/revenue/band",
        params,
      }),
      transformResponse: (res: any) => pickArray(res),
    }),

    /* =====================
   CHAPTER TREASURER
===================== */

    getChapterTreasurerDashboard: builder.query<
      ChapterTreasurerDashboard,
      number
    >({
      query: (chapterId) => `/finance/chapter-dashboard/${chapterId}`,
    }),

    /* =====================
   MEMBER FINANCIAL STATEMENT
===================== */

    getMemberFinancialStatement: builder.query<MemberStatement, number>({
      query: (memberId) => `/finance/member-statement/${memberId}`,
    }),

    /* =====================
   EXPORT REPORTS
===================== */

    exportFinanceCSV: builder.query<any, any>({
      query: (params) => ({
        url: "/finance/export/csv",
        params,
      }),
    }),

    exportFinancePDF: builder.query<any, any>({
      query: (params) => ({
        url: "/finance/export/pdf",
        params,
      }),
    }),

    sendReminderToDefaulters: builder.mutation<any, any>({
      query: (body) => ({
        url: "/finance/send-reminders",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ReminderLogs"],
    }),

    getReminderLogs: builder.query<any, { batch_id?: string }>({
      query: (params) => ({ url: "/finance/reminder-logs", params }),
      providesTags: ["ReminderLogs"],
    }),

    /* =====================
   FINANCE LEDGER
===================== */

    getMembersFinancialStatus: builder.query<any, any>({
      query: (params) => ({
        url: "/finance/status",
        params,
      }),

      transformResponse: (res: any) => ({
        data: res.data,
        total_balance: res.total_balance ?? 0,
        meta: {
          current_page: res.current_page,
          last_page: res.last_page,
          per_page: res.per_page,
          total: res.total,
          links: res.links,
        },
      }),
    }),

    getMyPayments: builder.query<any[], void>({
      query: () => "/my-payments",
    }),

    reverifyPayment: builder.mutation<any, string>({
      query: (id) => ({
        url: `/admin/billing/payments/${id}/reverify`,
        method: "POST",
      }),
      invalidatesTags: ["Payments"],
    }),

    // Settlement already emails the member their receipt automatically. This
    // recovers the ones queued while the queue worker was down — nothing
    // retries those, so without it the secretariat had no remedy.
    // Changes nothing about the payment, so it invalidates no tags.
    resendPaymentReceipt: builder.mutation<any, string>({
      query: (id) => ({
        url: `/admin/billing/payments/${id}/resend-receipt`,
        method: "POST",
      }),
    }),

    // Correct the gateway reference on a payment that has not settled.
    //
    // Direct rather than an amendment request: an unsettled payment has no
    // receipt or ledger posting to rewrite, and a wrong reference is exactly
    // what makes Reverify report "no record". The API refuses anything 'paid'.
    //
    // Invalidates Payments so the register shows the corrected reference the
    // admin then has to reverify against.
    updatePaymentReference: builder.mutation<
      any,
      { id: string; body: { reference: string; reason?: string } }
    >({
      query: ({ id, body }) => ({
        url: `/admin/billing/payments/${id}/reference`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Payments"],
    }),

    getLedgerAccounts: builder.query<any[], void>({
      query: () => "/admin/billing/ledger-accounts",
      transformResponse: (res: any) => pickArray(res),
      providesTags: ["LedgerAccounts"],
    }),

    /* =====================
     * PAYMENT EDIT / DELETE REQUESTS
     * ===================== */

    requestPaymentEdit: builder.mutation<
      any,
      {
        id: string | number;
        body: {
          reason: string;
          amount?: number;
          payment_method?: string;
          paid_at?: string;
          reference?: string;
          // The account the money was received into — approving the request
          // also moves the ledger credit onto it.
          bank_account_id?: number;
          notes?: string;
        };
      }
    >({
      query: ({ id, body }) => ({
        url: `/admin/billing/payments/${id}/edit-request`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "PaymentEditRequests" as const, id: "LIST" }],
    }),

    requestPaymentDelete: builder.mutation<
      any,
      { id: string | number; body: { reason: string } }
    >({
      query: ({ id, body }) => ({
        url: `/admin/billing/payments/${id}/delete-request`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "PaymentEditRequests" as const, id: "LIST" }],
    }),

    getPaymentEditRequests: builder.query<
      any,
      { status?: string; page?: number; per_page?: number }
    >({
      query: (params) => ({
        url: "/admin/billing/payment-edit-requests",
        params,
      }),
      providesTags: [{ type: "PaymentEditRequests" as const, id: "LIST" }],
    }),

    approvePaymentRequest: builder.mutation<
      any,
      { id: string | number; body?: { notes?: string } }
    >({
      query: ({ id, body }) => ({
        url: `/admin/billing/payment-edit-requests/${id}/approve`,
        method: "POST",
        body: body ?? {},
      }),
      invalidatesTags: [
        { type: "PaymentEditRequests" as const, id: "LIST" },
        "Payments",
      ],
    }),

    rejectPaymentRequest: builder.mutation<
      any,
      { id: string | number; body: { reason: string } }
    >({
      query: ({ id, body }) => ({
        url: `/admin/billing/payment-edit-requests/${id}/reject`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "PaymentEditRequests" as const, id: "LIST" }],
    }),

    getPaymentAuditTrail: builder.query<any, string | number>({
      query: (paymentId) =>
        `/admin/billing/payments/${paymentId}/audit-trail`,
      providesTags: (_r, _e, id) => [
        { type: "PaymentEditRequests" as const, id },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUserCategoriesQuery,
  useGetMembershipBandsQuery,
  useGetMyPaymentsQuery,
  useGetChargeTypesQuery,
  useGetChargeTypeDetailsQuery,
  useCreateChargeTypeMutation,
  useUpdateChargeTypeMutation,
  useDeleteChargeTypeMutation,

  useGetChargePricesQuery,
  useCreateChargePriceMutation,
  useUpdateChargePriceMutation,
  useDeleteChargePriceMutation,

  useGetMyDuesQuery,
  useGetUserDuesQuery,
  useGetMyFinanceSummaryQuery,
  useGetMyFinanceSummary2Query,
  useGetMyReceiptsQuery,
  useDownloadReceiptQuery,
  useGetUserFinanceSummaryQuery,
  useCreateInvoiceMutation,

  useGetInvoiceQuery,
  useGetUserInvoiceQuery,
  useGetInvoiceItemQuery,
  useMyInvoicesQuery,
  useUserInvoicesQuery,
  usePayInvoiceMutation,
  useGetPaymentQuery,
  useStartInvoicePaymentMutation,
  useStartDuesPaymentMutation,
  useUploadBankPaymentNowMutation,
  useGetDuesAnalysisQuery,
  useGetChaptersQuery,
  useGetStatesQuery,
  useGetFinanceDashboardQuery,

  useGetFinanceAnalyticsQuery,
  useGetRevenueByChapterQuery,
  useGetRevenueByStateQuery,
  useGetRevenueByBandQuery,

  useGetChapterTreasurerDashboardQuery,

  useGetMemberFinancialStatementQuery,

  useExportFinanceCSVQuery,
  useExportFinancePDFQuery,

  useGetMembersFinancialStatusQuery,
  useSendReminderToDefaultersMutation,
  useGetReminderLogsQuery,

  useAdminPayInvoiceMutation,
  useAdminRecordAdvancePaymentMutation,
  useAdminUserPaymentsQuery,
  useAdminPaymentIndexQuery,
  useLazyAdminPaymentIndexQuery,
  useLazyAdminPaymentExportQuery,
  useGetLedgerAccountsQuery,
  useReverifyPaymentMutation,
  useResendPaymentReceiptMutation,
  useUpdatePaymentReferenceMutation,
  useRequestPaymentEditMutation,
  useRequestPaymentDeleteMutation,
  useGetPaymentEditRequestsQuery,
  useApprovePaymentRequestMutation,
  useRejectPaymentRequestMutation,
  useGetPaymentAuditTrailQuery,
} = billingApi;
